import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Phone, Mail, MapPin, Wine, Send, Copy, Pencil, Trash2, Check,
  Package, Truck, Store, Loader2, CalendarDays, Undo2, History,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const prettyDate = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("fr-FR") : "—");

const SUB_STATUSES = [
  ["nouveau", "Nouveau"], ["rib_envoye", "RIB envoyé"], ["actif", "Actif"],
  ["suspendu", "Suspendu"], ["termine", "Terminé"],
];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [ribOpen, setRibOpen] = useState(false);
  const [ribMsg, setRibMsg] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [delOpen, setDelOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/subscribers/${id}`);
    setSub(data);
    if (data.orderId) {
      try {
        const h = await api.get(`/admin/orders/${data.orderId}/history`);
        setHistory(h.data);
      } catch { setHistory(null); }
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setSubStatus = async (status) => {
    await api.patch(`/admin/subscriptions/${id}/status`, { status });
    toast.success("Statut de l'abonnement mis à jour");
    load();
  };

  const setOrder = async (orderId, fields) => {
    await api.patch(`/admin/orders/${orderId}`, fields);
    toast.success("Échéance mise à jour");
    load();
  };

  const setOrderDate = async (orderId, date) => {
    await api.patch(`/admin/orders/${orderId}`, { scheduledDate: date });
    load();
  };

  const openRib = async () => {
    const { data } = await api.get(`/admin/subscribers/${id}/rib-message`);
    setRibMsg(data);
    setRibOpen(true);
  };

  const sendRib = () => {
    if (!ribMsg) return;
    api.patch(`/admin/subscriptions/${id}/status`, { status: "rib_envoye" }).then(load);
    window.location.href = `mailto:${ribMsg.email}?subject=${encodeURIComponent(ribMsg.subject)}&body=${encodeURIComponent(ribMsg.message)}`;
  };

  const copyMsg = async () => {
    await navigator.clipboard.writeText(ribMsg.message);
    toast.success("Message copié");
  };

  const openEdit = () => {
    setEdit({
      firstName: sub.customer.firstName, lastName: sub.customer.lastName,
      email: sub.customer.email, phone: sub.customer.phone,
      address: sub.customer.address, addressComplement: sub.customer.addressComplement,
      postalCode: sub.customer.postalCode, city: sub.customer.city,
      deliveryMethod: sub.deliveryMethod, notes: sub.notes,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/subscribers/${id}`, edit);
      toast.success("Fiche mise à jour");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const doDelete = async () => {
    await api.delete(`/admin/subscribers/${id}`);
    toast.success("Abonné supprimé");
    navigate("/admin/clients");
  };

  const resendLink = async () => {
    try {
      const { data } = await api.post(`/admin/orders/${sub.orderId}/resend-link`);
      toast.success(data.emailed ? "Lien de paiement renvoyé par email" : "Lien régénéré (email non envoyé)");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const doRefund = async () => {
    try {
      await api.post(`/admin/orders/${sub.orderId}/refund`);
      toast.success("Commande remboursée");
      setRefundOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!sub)
    return <div className="flex justify-center pt-24"><Loader2 className="w-7 h-7 animate-spin text-[#581825]" /></div>;

  const c = sub.customer;
  const setE = (k, v) => setEdit((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="client-detail-sheet" className="px-4 pt-5 pb-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#6E675F] mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-[#E5DEC9] p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-serif text-2xl font-bold text-[#110F0F]">{c.firstName} {c.lastName}</h1>
            <p className="text-sm text-[#6E675F] mt-0.5">Abonné #{sub.subscriberNumber} · inscrit le {new Date(sub.createdAt).toLocaleDateString("fr-FR")}</p>
          </div>
          <StatusBadge value={sub.status} />
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-sm text-[#581825] bg-[#F5EFEB] rounded-full px-3 py-1.5">
            <Phone className="w-4 h-4" /> {c.phone}
          </a>
          <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-sm text-[#581825] bg-[#F5EFEB] rounded-full px-3 py-1.5">
            <Mail className="w-4 h-4" /> {c.email}
          </a>
        </div>

        {sub.merchantReference && (
          <div className="mt-4 rounded-xl border border-[#E5DEC9] p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#6E675F]">Paiement en ligne (CAWL)</p>
                <p className="text-sm text-[#110F0F] mt-0.5">
                  {sub.orderAmount != null ? `${sub.orderAmount} €` : "—"}
                  {sub.orderPaidAt ? ` · payé le ${new Date(sub.orderPaidAt).toLocaleDateString("fr-FR")}` : ""}
                </p>
                <p className="text-[10px] text-[#8a847b] mt-0.5">Réf. {sub.merchantReference}</p>
              </div>
              {sub.orderPaymentStatus && <StatusBadge type="order_payment" value={sub.orderPaymentStatus} />}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {["pending", "failed", "cancelled"].includes(sub.orderPaymentStatus) && (
                <Button size="sm" variant="outline" onClick={resendLink} data-testid="btn-resend-link">
                  <Send className="w-3.5 h-3.5 mr-1" /> Renvoyer le lien de paiement
                </Button>
              )}
              {sub.orderPaymentStatus === "paid" && (
                <Button size="sm" variant="outline" onClick={() => setRefundOpen(true)} data-testid="btn-refund"
                  className="text-red-600 hover:text-red-700">
                  <Undo2 className="w-3.5 h-3.5 mr-1" /> Rembourser
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
          <div><p className="text-[#6E675F]">Formule</p><p className="font-semibold text-[#581825]">{sub.planLabel} · {sub.bottlesPerMonth} bt.</p></div>
          <div><p className="text-[#6E675F]">Prix</p><p className="font-semibold text-[#110F0F]">{sub.monthlyPrice} € / mois</p></div>
          <div><p className="text-[#6E675F]">Récupération</p>
            <p className="font-semibold flex items-center gap-1 text-[#110F0F]">
              {sub.deliveryMethod === "livraison" ? <Truck className="w-4 h-4" /> : <Store className="w-4 h-4" />}
              {sub.deliveryMethod === "livraison" ? "Livraison" : "Retrait"}
            </p>
          </div>
          <div><p className="text-[#6E675F]">Progression</p><p className="font-semibold text-[#110F0F]">{sub.completedMonths} / 6 mois</p></div>
          <div><p className="text-[#6E675F]">Prochaine date</p><p className="font-semibold text-[#110F0F]">{prettyDate(sub.nextDate)}</p></div>
          <div><p className="text-[#6E675F]">Offre lancement</p><p className="font-semibold text-[#110F0F]">{sub.launchOfferEligible ? "Oui 🎁" : "Non"}</p></div>
        </div>

        {sub.deliveryMethod === "livraison" && (c.address || c.city) && (
          <div className="mt-4 flex items-start gap-2 text-sm text-[#6E675F] bg-[#F5EFEB] rounded-xl p-3">
            <MapPin className="w-4 h-4 mt-0.5 text-[#581825]" />
            <span>{c.address}{c.addressComplement ? `, ${c.addressComplement}` : ""}<br />{c.postalCode} {c.city}</span>
          </div>
        )}
        {sub.notes && <p className="mt-3 text-sm text-[#6E675F] italic">« {sub.notes} »</p>}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2.5 mt-4">
        <Button data-testid="btn-send-rib" onClick={openRib} className="bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7] h-11">
          <Send className="w-4 h-4 mr-2" /> Envoyer le RIB
        </Button>
        <Button asChild variant="outline" className="h-11">
          <a href={`tel:${c.phone}`}><Phone className="w-4 h-4 mr-2" /> Appeler</a>
        </Button>
        <Button asChild variant="outline" className="h-11">
          <a href={`mailto:${c.email}`}><Mail className="w-4 h-4 mr-2" /> Email</a>
        </Button>
        <Button variant="outline" onClick={openEdit} data-testid="btn-edit-client" className="h-11">
          <Pencil className="w-4 h-4 mr-2" /> Modifier
        </Button>
      </div>

      {/* Subscription status changer */}
      <div className="mt-5">
        <p className="text-xs uppercase tracking-wider text-[#6E675F] mb-2">Statut de l'abonnement</p>
        <div className="flex flex-wrap gap-2">
          {SUB_STATUSES.map(([v, t]) => (
            <button key={v} data-testid="btn-status-toggle" onClick={() => setSubStatus(v)}
              className={`rounded-full px-3 py-1.5 text-sm border ${sub.status === v ? "bg-[#581825] text-[#FDFBF7] border-[#581825]" : "bg-white text-[#6E675F] border-[#E5DEC9]"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Monthly orders */}
      <div className="mt-6">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-2">
          <Package className="w-5 h-5" /> Suivi des 6 mois
        </h2>
        <div className="space-y-2.5">
          {sub.orders.map((o) => (
            <div key={o.id} className="bg-white rounded-xl border border-[#E5DEC9] p-3">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-[#110F0F]">{o.monthLabel}</p>
                <span className="text-sm font-semibold text-[#581825]">{o.amount} €</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <StatusBadge type="payment" value={o.paymentStatus} />
                <StatusBadge type="prep" value={o.preparationStatus} />
                <div className="ml-auto flex items-center gap-1 text-xs text-[#6E675F]">
                  <CalendarDays className="w-3.5 h-3.5" />
                  <input type="date" value={o.scheduledDate}
                    onChange={(e) => setOrderDate(o.id, e.target.value)}
                    className="bg-transparent border border-[#E5DEC9] rounded px-1.5 py-0.5" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2.5">
                {o.paymentStatus !== "paye" ? (
                  <Button size="sm" variant="outline" onClick={() => setOrder(o.id, { paymentStatus: "paye" })}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Marquer payé
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setOrder(o.id, { paymentStatus: "en_attente" })}>Annuler paiement</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setOrder(o.id, { preparationStatus: "pret" })}>Prêt</Button>
                {sub.deliveryMethod === "livraison"
                  ? <Button size="sm" variant="outline" onClick={() => setOrder(o.id, { preparationStatus: "livre" })}>Livré</Button>
                  : <Button size="sm" variant="outline" onClick={() => setOrder(o.id, { preparationStatus: "retire" })}>Retiré</Button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {history && history.items.length > 0 && (
        <div className="mt-6" data-testid="payment-history">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-2">
            <History className="w-5 h-5" /> Historique des paiements
          </h2>
          <div className="bg-white rounded-2xl border border-[#E5DEC9] p-4">
            <ol className="relative border-l border-[#E5DEC9] ml-2">
              {history.items.map((it, idx) => {
                const dotColor =
                  it.type === "notification" ? "bg-[#C5A059]"
                  : it.status === "paid" ? "bg-emerald-500"
                  : it.status === "refunded" ? "bg-blue-500"
                  : it.status === "failed" ? "bg-red-500"
                  : it.status === "cancelled" ? "bg-stone-400"
                  : "bg-[#581825]";
                const when = it.at
                  ? new Date(it.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                  : "—";
                return (
                  <li key={idx} className="ml-4 pb-4 last:pb-0">
                    <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${dotColor} border-2 border-white`} />
                    <p className="text-sm font-semibold text-[#110F0F]">
                      {it.label}
                      {it.amount != null ? <span className="text-[#581825]"> · {it.amount} €</span> : null}
                    </p>
                    <p className="text-xs text-[#6E675F]">
                      {when}{it.to ? ` · ${it.to}` : ""}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}

      <Button variant="ghost" onClick={() => setDelOpen(true)} data-testid="btn-delete-client"
        className="mt-6 w-full text-red-600 hover:text-red-700 hover:bg-red-50">
        <Trash2 className="w-4 h-4 mr-2" /> Supprimer cet abonné
      </Button>

      {/* RIB dialog */}
      <Dialog open={ribOpen} onOpenChange={setRibOpen}>
        <DialogContent className="max-w-lg bg-[#FDFBF7]">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#581825]">Message RIB</DialogTitle></DialogHeader>
          <Textarea value={ribMsg?.message || ""} readOnly rows={12} className="text-sm bg-white" />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={copyMsg} data-testid="btn-copy-message" className="flex-1">
              <Copy className="w-4 h-4 mr-2" /> Copier le message
            </Button>
            <Button onClick={sendRib} className="flex-1 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7]">
              <Send className="w-4 h-4 mr-2" /> Envoyer par email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] top-0 left-0 translate-x-0 translate-y-0 rounded-none overflow-y-auto no-scrollbar bg-[#FDFBF7] p-5 pb-56 sm:p-6 sm:pb-6 sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:top-[50%] sm:left-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#581825]">Modifier la fiche</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom</Label><Input value={edit.firstName || ""} onChange={(e) => setE("firstName", e.target.value)} /></div>
              <div><Label>Nom</Label><Input value={edit.lastName || ""} onChange={(e) => setE("lastName", e.target.value)} /></div>
            </div>
            <div><Label>Email</Label><Input value={edit.email || ""} onChange={(e) => setE("email", e.target.value)} /></div>
            <div><Label>Téléphone</Label><Input value={edit.phone || ""} onChange={(e) => setE("phone", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              {[["retrait", "Retrait"], ["livraison", "Livraison"]].map(([v, t]) => (
                <button key={v} onClick={() => setE("deliveryMethod", v)}
                  className={`rounded-lg border p-2 text-sm ${edit.deliveryMethod === v ? "border-[#581825] bg-[#F5EFEB]" : "border-[#E5DEC9]"}`}>{t}</button>
              ))}
            </div>
            {edit.deliveryMethod === "livraison" && (
              <>
                <div><Label>Adresse</Label><Input value={edit.address || ""} onChange={(e) => setE("address", e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Code postal</Label><Input value={edit.postalCode || ""} onChange={(e) => setE("postalCode", e.target.value)} /></div>
                  <div><Label>Ville</Label><Input value={edit.city || ""} onChange={(e) => setE("city", e.target.value)} /></div>
                </div>
              </>
            )}
            <div><Label>Commentaire</Label><Textarea value={edit.notes || ""} onChange={(e) => setE("notes", e.target.value)} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} className="bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7] w-full">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="bg-[#FDFBF7]">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet abonné ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprime définitivement {c.firstName} {c.lastName} et ses 6 échéances. Irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={refundOpen} onOpenChange={setRefundOpen}>
        <AlertDialogContent className="bg-[#FDFBF7]">
          <AlertDialogHeader>
            <AlertDialogTitle>Rembourser cette commande ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le montant de {sub.orderAmount} € sera remboursé au client via CAWL. Action irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doRefund} className="bg-red-600 hover:bg-red-700">Rembourser</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
